import { useMemo } from 'react'

import { EyeInvisible, EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { SPACE } from 'modules/documents/lib/constants'
import { isCompanyPublicSpace } from 'modules/documents/lib/Documents'

import type { DocumentNode } from './types'

export function useColumns(space: SPACE) {
  const isPublicSpace = isCompanyPublicSpace(space)

  const columns = useMemo(
    () => [
      {
        label: '',
        width: 45,
        render: (item: DocumentNode) => {
          if (!item.contentType) return { type: 'text' as const, value: undefined }

          return {
            type: 'file' as const,
            value: {
              name: item.filename,
              type: item.contentType.split('/').pop() ?? '',
            },
          }
        },
      },
      {
        label: i18n.t('documents.employee.list.filename'),
        sorting: 'filename' as const,
        width: 400,
        render: (item: DocumentNode) => ({
          type: 'text' as const,
          value: item.filename,
        }),
      },
      {
        label: i18n.t('documents.employee.list.uploaded_by'),
        render: (item: DocumentNode) => {
          if (!item.author?.fullName) {
            return { type: 'text' as const, value: '-' }
          }

          return {
            type: 'person' as const,
            value: {
              firstName: item.author.fullName,
              lastName: '',
              src: item.author.avatar ? buildUrl(item.author.avatar) : undefined,
            },
          }
        },
      },
      {
        label: i18n.t('documents.employee.list.created_at'),
        sorting: 'createdAt' as const,
        width: 150,
        render: (item: DocumentNode) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(item.createdAt),
        }),
      },
      ...(isPublicSpace
        ? [
            {
              label: ' ',
              width: 45,
              render: (item: DocumentNode) => {
                const isVisible = item.public

                return {
                  type: 'icon' as const,
                  value: {
                    icon: isVisible ? EyeVisible : EyeInvisible,
                    label: isVisible
                      ? i18n.t('documents.employee.filters.visible')
                      : i18n.t('documents.employee.filters.hidden'),
                  },
                }
              },
            },
            {
              label: ' ',
              width: 45,
              render: (item: DocumentNode) => {
                if (item.status !== 'to_fill') {
                  return { type: 'text' as const, value: undefined }
                }

                return {
                  type: 'icon' as const,
                  value: {
                    icon: Pencil,
                    label: i18n.t('documents.employee.list.to_fill'),
                  },
                }
              },
            },
          ]
        : []),
    ],
    [isPublicSpace]
  )

  return {
    sortings: {
      filename: {
        label: i18n.t('documents.employee.list.filename'),
      },
      createdAt: {
        label: i18n.t('documents.employee.list.created_at'),
      },
    },
    defaultSortings: {
      field: 'createdAt' as const,
      order: 'desc' as const,
    },
    columns,
  }
}
